import React, { useEffect, useRef } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import classnames from 'classnames';
import styles from './Raft.module.css';

const usePrevious = (value: string): string | undefined => {
  const ref = useRef<string>();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

interface RaftProps {
  size:
    | 'toolbar' // Narrow toolbar with minimal content before being used.
    | 'engaged-top' // User has engaged with the assistant (e.g. focused the input).
    | 'engaged-bottom' // User has engaged with the assistant (e.g. focused the input).
    | 'full-height' // Assistant has responses and takes full height of page.
    | 'side-panel' // Assistant is in a side panel.
    | 'full-screen' // Assistant takes over the whole screen.
    | 'full-page'; // Assistant takes over the whole page except the nav bar.

  children: React.ReactNode;

  // In the artifact viewer, we don't want to use popover.
  noPopover?: boolean;
}

// The "raft" floats the assistant above and around the rest of the page.
//
// The raft dynamically resizes based on user actions and may dynamically position
// based on the underlying page content. Everything inside the raft should
// responsively handle the resizing.
const Raft = ({ size, children, noPopover = false }: RaftProps) => {
  const raftRef = React.useRef<HTMLDivElement>(null);
  const previousSize = usePrevious(size);

  // Use popover to make full-screen raft appear above the rest of the page.
  const bringToTop = () => {
    const node = raftRef.current;
    if (node) {
      if (size !== 'side-panel' && !noPopover) {
        if (!node.hasAttribute('popover')) {
          node.setAttribute('popover', 'manual');
          node.showPopover();
        } else {
          // Bring it back to the top.
          node.hidePopover();
          node.showPopover();
        }
      } else if (node.hasAttribute('popover')) {
        node.hidePopover();
        node.removeAttribute('popover');
      }
    }
  };

  useEffect(
    () => bringToTop(),
    // We only want it to run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Special handling for when the size changes when in a sidepanel like the editor side panel.
  // To ensure it pops over the top of the editor toolbar.
  useEffect(() => {
    if (previousSize !== size) {
      if (
        (previousSize === 'side-panel' && size === 'full-screen') ||
        (previousSize === 'full-screen' && size === 'side-panel')
      ) {
        bringToTop();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousSize, size]);

  return (
    <div
      className={classnames(styles.raft, `raft-size-${size}`, {
        [styles.raftFloating]: size !== 'side-panel' && size !== 'full-page',
        [styles.raftToolbar]: size === 'toolbar',
        [styles.raftEngagedTop]: size === 'engaged-top',
        [styles.raftEngagedBottom]: size === 'engaged-bottom',
        [styles.raftFullHeight]: size === 'full-height',
        [styles.raftSidePanel]: size === 'side-panel',
        [styles.raftFullScreen]: size === 'full-screen',
        [styles.raftFullPage]: size === 'full-page',
      })}
      ref={raftRef}
    >
      {children}
    </div>
  );
};

export default ApplicationView(Raft);
